import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../utils/cartTotals';

const getItemKey = (item) =>
  `${item.product_id}-${item.product_variant_id ?? 'base'}-${item.variant_id ?? 'base'}`;

const isOutOfStock = (item) => {
  if (item.in_stock === false) return true;
  if (item.stock !== undefined && item.stock !== null && Number(item.stock) === 0) return true;
  return false;
};

const resolveVariant = (item) => {
  if (typeof item.variant === 'string' && item.variant.trim()) return item.variant.trim();
  const n = item.variant_name ?? '';
  const u = item.unit_name ?? item.variant_unit ?? '';
  if (n || u) return `${n} ${u}`.trim();
  if (item.variant && typeof item.variant === 'object') {
    const vn = item.variant.variant_name ?? '';
    const vu = item.variant.unit_name ?? '';
    if (vn || vu) return `${vn} ${vu}`.trim();
  }
  return null;
};

// Best line total: server item_total → price × qty
const lineTotal = (item) => {
  if (item.item_total && parseFloat(item.item_total) > 0) return parseFloat(item.item_total);
  return parseFloat(item.price || 0) * parseInt(item.quantity || 1, 10);
};

const ReviewStep = ({ form, cart, userAddresses = [], selectedAddressId, onEditAddress, onNext }) => {
  const navigate = useNavigate();
  const oosItems = cart.filter(isOutOfStock);
  const hasOos = oosItems.length > 0;

  const resolvedAddress = (() => {
    if (userAddresses.length > 0) {
      const byId = selectedAddressId
        ? userAddresses.find((a) => String(a.address_id ?? a.id) === String(selectedAddressId))
        : null;
      const addr = byId || userAddresses.find((a) => a.is_default) || userAddresses[0];
      if (addr) {
        return {
          fullName:     addr.full_name ?? addr.fullName ?? '',
          phone:        addr.phone ?? addr.mobile ?? '',
          addressLine1: addr.address_line1 ?? addr.addressLine1 ?? addr.address ?? '',
          addressLine2: addr.address_line2 ?? addr.addressLine2 ?? '',
          city:         addr.city ?? '',
          state:        addr.state ?? '',
          pincode:      addr.pincode ?? addr.zip ?? '',
        };
      }
    }
    return form;
  })();

  const join = (...parts) => parts.filter(Boolean).join(', ');

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-black/5 sm:p-8">
      <h2 className="mb-6 flex items-center gap-3 text-xl font-extrabold text-gray-900">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-black text-primary-600">
          2
        </span>
        Review Your Order
      </h2>

      {/* Out-of-stock warning */}
      {hasOos && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-3">
          <span className="mt-0.5 text-base">⚠️</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-red-700">Some items are out of stock</p>
            <p className="mt-0.5 text-xs text-red-500">
              {oosItems.map((i) => i.name).join(', ')} — please remove them before continuing.
            </p>
            <button
              type="button"
              onClick={() => navigate('/cart')}
              className="mt-2 rounded-lg bg-red-100 px-3 py-1 text-xs font-bold text-red-700 hover:bg-red-200"
            >
              Go to Cart → Remove item
            </button>
          </div>
        </div>
      )}

      {/* Delivery address */}
      <div className="mb-6 rounded-2xl border border-gray-100 bg-gray-50 p-4">
        <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">Delivering to</p>
        {resolvedAddress.fullName && (
          <p className="font-bold text-gray-900">{resolvedAddress.fullName}</p>
        )}
        {(resolvedAddress.addressLine1 || resolvedAddress.addressLine2) && (
          <p className="text-sm text-gray-600">
            {join(resolvedAddress.addressLine1, resolvedAddress.addressLine2)}
          </p>
        )}
        {(resolvedAddress.city || resolvedAddress.state || resolvedAddress.pincode) && (
          <p className="text-sm text-gray-600">
            {join(resolvedAddress.city, resolvedAddress.state)}
            {resolvedAddress.pincode ? ` – ${resolvedAddress.pincode}` : ''}
          </p>
        )}
        {resolvedAddress.phone && (
          <p className="text-sm text-gray-500">{resolvedAddress.phone}</p>
        )}
        {!resolvedAddress.fullName && !resolvedAddress.addressLine1 && (
          <p className="text-sm italic text-gray-400">No address selected. Click Edit to add one.</p>
        )}
        <button
          type="button"
          onClick={onEditAddress}
          className="mt-2 text-xs font-bold text-primary-600 hover:underline"
        >
          Edit Address
        </button>
      </div>

      {/* Cart items — fully dynamic */}
      {cart.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-gray-200 py-10 text-center">
          <svg className="h-10 w-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="text-sm font-semibold text-gray-400">No items in cart</p>
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="rounded-xl bg-primary-600 px-5 py-2 text-sm font-bold text-white hover:bg-primary-700"
          >
            Browse Products
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {cart.map((item) => {
            const oos = isOutOfStock(item);
            const variant = resolveVariant(item);
            return (
              <div
                key={getItemKey(item)}
                className={`flex items-center gap-4 rounded-2xl border p-4 ${
                  oos ? 'border-red-100 bg-red-50/40' : 'border-gray-100 bg-white'
                }`}
              >
                {/* Image */}
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-gray-50 p-2">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className={`h-full w-full object-contain ${oos ? 'opacity-40' : ''}`}
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No img</span>
                  )}
                </div>

                {/* Details */}
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-1 font-semibold text-gray-900">{item.name}</p>
                  {variant && (
                    <span className="mt-0.5 inline-block rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                      {variant}
                    </span>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                    <span>Qty: {item.quantity}</span>
                    <span className="text-gray-300">·</span>
                    <span>₹{parseFloat(item.price || 0).toFixed(2)} each</span>
                    {item.gst > 0 && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span>GST {Number(item.gst).toFixed(2)}%</span>
                      </>
                    )}
                  </div>
                  {oos && (
                    <span className="mt-1 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600">
                      Out of Stock
                    </span>
                  )}
                </div>

                {/* Line total */}
                <p className={`shrink-0 text-sm font-bold ${oos ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                  {formatCurrency(lineTotal(item))}
                </p>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={hasOos || cart.length === 0}
        className="mt-8 w-full rounded-2xl bg-primary-600 py-4 text-base font-bold text-white shadow-lg shadow-primary-600/30 transition-all hover:-translate-y-0.5 hover:bg-primary-700 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
      >
        Continue to Payment →
      </button>
    </div>
  );
};

export default ReviewStep;
